import { AfterViewInit, Component, EventEmitter, Input, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { FormControl, FormControlName, FormGroup, FormGroupDirective } from '@angular/forms';
import { BehaviorSubject, debounceTime, distinctUntilChanged, Observable, of, switchMap, tap } from 'rxjs';
import { STRING_CONSTANTS } from '@config/constants/string.constants';
import { catchError, finalize, map } from 'rxjs/operators';
import { BaseResponse } from '@shared/base/base-response';
import { IInputSelectOptions } from '@shared/components/v1/input/input-select-autocomplete/input-select-autocomplete.interface';
import { IChips, ISelectOptionsToChips } from '@shared/components/v1/chips/chips.interface';
import { ChipsComponent } from '@shared/components/v1/chips/chips.component';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

@Component({
  selector: 'app-input-select-autocomplete',
  templateUrl: './input-select-autocomplete.component.html',
  styleUrls: ['./input-select-autocomplete.component.scss'],
  providers: [MatAutocompleteTrigger],
})
export class InputSelectAutocompleteComponent implements OnInit, AfterViewInit {
  @Input() fetchDataFn!: (...args: any) => Observable<BaseResponse<IInputSelectOptions[]>> | undefined;
  @Input() optionLabelFn!: (option: IInputSelectOptions) => string;
  // Opsional, beda dari optionLabelFn — optionLabelFn juga dipakai buat baris di dropdown list
  // (getOptionLabel), sedangkan ini CUMA buat teks yang tampil di closed input pas ada value
  // terpilih (mis. gabung "label, area"), biar dropdown list tetap 2 baris terpisah (label bold +
  // description abu-abu) walau closed inputnya digabung. Consumer yang gak set ini gak kena
  // dampak apapun (fallback ke getOptionLabel, behavior lama).
  @Input() displayLabelFn!: (option: IInputSelectOptions) => string;

  @Input() label = '';
  @Input() icon!: string;
  @Input() placeholder = '';

  @Input() readOnly = false;
  @Input() useChips = false;
  @Input() required = false;
  @Input() useCustomLabel = false;

  @Input() optionsData!: IInputSelectOptions[];
  @Input() formControl!: FormControl;
  @Input() usePagination = true;
  @Input() searchParamKey: string = 'string_filter'; // Dynamic search key

  // Opt-in: mengetik ulang setelah ada pilihan membuang pilihan lama, supaya formControl tidak
  // menyimpan nilai yang beda dari teks yang terlihat.
  @Input() clearSelectionOnType = false;

  @Output() selectedValue = new EventEmitter<IInputSelectOptions>();
  @Output() selectionCleared = new EventEmitter<void>();

  // params
  stringFilter!: string;
  pageSize = 20;
  totalData = 20;
  currentPage = 0;
  hasNext? = false;

  noResults = new BehaviorSubject(true);
  isLoading = new BehaviorSubject(false);

  searchControl = new FormControl();
  filteredOptions$ = new BehaviorSubject([] as IInputSelectOptions[]);
  options = <IInputSelectOptions[]>[];
  // master list buat mode !usePagination — `options` sendiri ke-overwrite jadi hasil filter
  // di subscribe handleSearchChanges(), jadi gak bisa dipakai lagi sebagai sumber filter berikutnya
  private allOptions = <IInputSelectOptions[]>[];

  hasSelectedChips = new BehaviorSubject(false);

  private selectedValueSubject = new BehaviorSubject({} as IInputSelectOptions);
  private params: Record<string, any> = {};

  @ViewChild('chipsListRef') chipsListRef: ChipsComponent;
  @ViewChild(MatAutocompleteTrigger) autoCompleteTrigger!: MatAutocompleteTrigger;

  inputFormGroup?: FormGroup;
  inputFormControl?: FormControl;

  get SelectedValue() {
    return this.selectedValueSubject.value;
  }

  set SelectedValue(val) {
    this.selectedValueSubject.next(val);
  }

  constructor(
    @Optional() private formGroupDirective: FormGroupDirective, // Make optional if not always present
    @Optional() private formControlNameDirective: FormControlName // Make optional if not always present
  ) {}

  // ControlValueAccessor methods
  // onChange: any = () => {};
  // onTouched: any = () => {};

  ngOnInit() {
    this.loadInitialData();
    this.handleSearchChanges();
  }

  loadInitialData() {
    if (this.optionsData) return this.setInitOptionsData(this.optionsData);
    if (!this.fetchDataFn) return;
    else return this.fetchData();
  }

  private clearSelectionWhenTyping() {
    this.searchControl.valueChanges.subscribe((val) => {
      // Memilih opsi menulis objek ke input; hanya string = user mengetik.
      if (!this.clearSelectionOnType || typeof val !== 'string' || !this.hasSelectedValue()) return;
      this.SelectedValue = {} as IInputSelectOptions;
      this.formControl?.setValue(null);
      this.selectionCleared.emit();
    });
  }

  handleSearchChanges() {
    this.clearSelectionWhenTyping();
    this.searchControl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((val) => {
          const _isValidQuery = !!val && val.length >= 3;
          this.isLoading.next(_isValidQuery);
          if (!_isValidQuery) {
            this.filteredOptions$.next([]);
            this.noResults.next(false);
          }
        }),
        switchMap((val: string) => {
          this.currentPage = 0;

          const _isControlDirty = this.searchControl.dirty;
          if (_isControlDirty && val === '') {
            this.stringFilter = val;
            this.isLoading.next(true);
          }

          if (val.length > 0 && val.length < 3) return of(this.options);
          return this.filterOptions(val).pipe(catchError(() => of([])));
        }),
        tap((res) => {
          if (res !== null) {
            this.noResults.next(!res.length);
            this.isLoading.next(false);
          }
        })
      )
      .subscribe((filteredOptions) => {
        if (filteredOptions !== null) {
          this.options = filteredOptions;
          this.filteredOptions$.next(filteredOptions);
        }
      });
  }

  filterOptions(q: string) {
    if (q !== null && typeof q === 'object') return of(this.options);
    this.stringFilter = (q || '').trim().toLowerCase();

    if (this.usePagination) return this.fetchSearchData(this.stringFilter) as Observable<IInputSelectOptions[]>;

    const _filtered = this.allOptions.filter((option) => option.label.toLowerCase().includes(this.stringFilter));
    this.noResults.next(_filtered.length === 0);

    return of(_filtered);
  }

  fetchData(page: number = 0, size: number = this.pageSize, string_filter = this.stringFilter) {
    this.isLoading.next(true);
    const _params = {
      ...this.params,
      page,
      size,
      [this.searchParamKey]: string_filter,
    };

    this.fetchDataFn(_params)?.subscribe((resp) => {
      if (!resp) return;
      const { data, total_data, current_page, has_next } = resp;

      this.updateOptions(data);
      this.updatePaginationState({ total_data, current_page, has_next });

      // if has input value from parent, emit back so parent has complete object format
      if (!!this.inputFormControl?.value) {
        const selectedItem = this.inputFormControl?.value;
        const selectedOptionItem = data.find((option) => option.value === selectedItem.value);
        if (selectedOptionItem) this.onOptionSelected(selectedOptionItem);
      }

      this.noResults.next(!data.length);
      this.isLoading.next(false);
    });
  }

  fetchSearchData(q: string) {
    const _params = {
      ...this.params,
      page: this.currentPage,
      size: this.pageSize,
      [this.searchParamKey]: q,
    };

    return this.fetchDataFn?.(_params)?.pipe(
      map((resp) => {
        if (!resp) return null;
        this.updatePaginationState(resp);
        return resp.data;
      }),
      catchError(() => of([])),
      finalize(() => this.isLoading.next(false))
    );
  }

  private updatePaginationState(resp: { total_data: number; current_page: number; has_next?: boolean }) {
    const { total_data, current_page, has_next } = resp;
    this.totalData = total_data;
    this.currentPage = current_page;
    this.hasNext = has_next;
  }

  private updateOptions(data: IInputSelectOptions[]) {
    this.options = this.options.concat(data);
    this.options = [...new Map(this.options.map((item) => [item.value, item])).values()]; //remove any duplicated
    this.setFilteredOptions();
  }

  onOptionSelected(selectedOption: IInputSelectOptions) {
    this.SelectedValue = selectedOption;
    this.selectedValue.emit(this.SelectedValue);

    this.setFormControlValue();

    // set selected chips value
    if (this.useChips && this.hasSelectedValue()) this.handleSetChipsValue();

    this.resetFilteredOptions();
  }

  clearSearchInput = () => this.searchControl.setValue('');

  setDisableSearchInput = () => this.searchControl.disable();

  setInitOptionsData(data: IInputSelectOptions[]) {
    this.options = data;
    this.allOptions = data;
    this.totalData = data.length;
    this.setFilteredOptions();
  }

  setFilteredOptions = () => this.filteredOptions$.next(this.options);

  resetFilteredOptions() {
    this.isLoading.next(true);
    if (this.useChips) {
      this.clearSearchInput();
      this.filteredOptions$.next(this.options);
    }

    setTimeout(() => this.isLoading.next(false), 250);
  }

  getOptionLabel = (option: IInputSelectOptions) => (this.optionLabelFn ? this.optionLabelFn(option) : this.defaultDisplayLabel(option));

  displayFn = (option: IInputSelectOptions) => (this.displayLabelFn ? this.displayLabelFn(option) : this.getOptionLabel(option));

  defaultDisplayLabel = (opt: IInputSelectOptions) => opt && opt.label;

  hasSelectedValue = () => !!Object.keys(this.SelectedValue).length;

  handleSetChipsValue() {
    const currentChipsList = this.chipsListRef.ChipsListData;

    currentChipsList.push(this.mapSelectedOptionToChips(this.SelectedValue));
    this.chipsListRef.ChipsListData = currentChipsList;
  }

  mapSelectedOptionToChips(data: IInputSelectOptions) {
    const option = new ISelectOptionsToChips();
    option.setChips(data);
    return option;
  }

  handleSelectedChips(e: IChips[]) {
    this.hasSelectedChips.next(!!e.length);
    if (!e.length) return this.loadInitialData();
  }

  setParams = (params: Record<string, any>) => (this.params = params);

  loadMoreData() {
    // this.options.length >= this.totalData ||
    if (!this.hasNext) return;
    this.currentPage++;
    this.fetchData(this.currentPage);
  }

  isIntersecting(endOfList: boolean) {
    if (endOfList && this.usePagination) return this.loadMoreData();
  }

  setFormControlValue() {
    if (this.formControl) this.formControl.setValue(this.SelectedValue);
  }

  ngAfterViewInit() {
    this.setFormControlValue();
    // formGroupDirective/formControlNameDirective keduanya @Optional() - komponen ini bisa dipasang
    // TANPA formControlName sama sekali (pola sengaja dipakai buat hindarin bug DefaultValueAccessor
    // bubble event, lihat buat-pencairan.component.ts). getControl() Angular gak null-safe kalau
    // directive-nya null, jadi guard eksplisit di sini - tanpa guard ini crash tiap kali komponen
    // dipasang tanpa formControlName.
    if (!this.formGroupDirective || !this.formControlNameDirective) return;
    this.inputFormControl = this.formGroupDirective.getControl(this.formControlNameDirective);
    if (this.inputFormControl?.value) {
      this.searchControl.setValue(this.inputFormControl.value);
      setTimeout(() => this.onOptionSelected(this.inputFormControl?.value)); // safe to remove?
    }
  }

  clearOptions = () => (this.options = []);

  protected readonly STRING_CONSTANTS = STRING_CONSTANTS;
}
